import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { CheckCircle2, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import apiClient from '@/api/client';
import { completeUserModule } from '@/api/module';
import { useNoGuard, useNoGuardNavigate } from '@/hooks/useNoGuard';

const SURVEY_TYPE_MAP = {
  pulse: { id: 1, name: 'PULSE', description: 'Organizational Change Readiness' },
  shape: { id: 2, name: 'SHAPE', description: 'Personality Profile' }
} as const;

interface CategoryScore {
  categoryId: number;
  categoryName: string;
  score: number;
  maxScore: number;
  percentage: number;
  questionCount: number;
}

// Generate mock scores for noguard mode
function generateMockScores(surveyId: number): CategoryScore[] {
  if (surveyId === 1) {
    // Pulse survey categories
    return [
      { categoryId: 1, categoryName: 'Understanding', score: 18, maxScore: 25, percentage: 72, questionCount: 5 },
      { categoryId: 2, categoryName: 'Support', score: 20, maxScore: 25, percentage: 80, questionCount: 5 },
      { categoryId: 3, categoryName: 'Impact', score: 16, maxScore: 25, percentage: 64, questionCount: 5 },
      { categoryId: 4, categoryName: 'Safety', score: 22, maxScore: 25, percentage: 88, questionCount: 5 },
      { categoryId: 5, categoryName: 'Readiness', score: 19, maxScore: 25, percentage: 76, questionCount: 5 },
      { categoryId: 6, categoryName: 'Resources', score: 17, maxScore: 25, percentage: 68, questionCount: 5 },
      { categoryId: 7, categoryName: 'Outcomes', score: 21, maxScore: 25, percentage: 84, questionCount: 5 },
      { categoryId: 8, categoryName: 'Leadership', score: 18, maxScore: 25, percentage: 72, questionCount: 5 },
    ];
  } else {
    // Shape survey categories
    return [
      { categoryId: 1, categoryName: 'Adaptive', score: 22, maxScore: 30, percentage: 73, questionCount: 6 },
      { categoryId: 2, categoryName: 'Integrity', score: 25, maxScore: 30, percentage: 83, questionCount: 6 },
      { categoryId: 3, categoryName: 'Collaborative', score: 24, maxScore: 30, percentage: 80, questionCount: 6 },
      { categoryId: 4, categoryName: 'Result', score: 20, maxScore: 30, percentage: 67, questionCount: 6 },
      { categoryId: 5, categoryName: 'Customer', score: 23, maxScore: 30, percentage: 77, questionCount: 6 },
      { categoryId: 6, categoryName: 'Detail', score: 18, maxScore: 30, percentage: 60, questionCount: 6 },
    ];
  }
}

export default function WorkingSurveyResults() {
  console.log('WorkingSurveyResults component mounting!');
  
  const params = useParams();
  const navigate = useNavigate();
  const noGuardNavigate = useNoGuardNavigate();
  const noGuard = useNoGuard();
  const [loading, setLoading] = useState(true);
  const [categoryScores, setCategoryScores] = useState<CategoryScore[]>([]);
  const [error, setError] = useState('');

  console.log('All params:', params);
  console.log('Survey type from URL:', params.surveyType);
  
  // For the specific routes, we need to hardcode the survey type
  const pathParts = window.location.pathname.split('/');
  const surveyType = params.surveyType || (pathParts.includes('pulse') ? 'pulse' : 'shape');
  console.log('Determined survey type:', surveyType);
  
  const survey = SURVEY_TYPE_MAP[surveyType as keyof typeof SURVEY_TYPE_MAP];

  useEffect(() => {
    console.log('useEffect running, survey:', survey);
    
    async function loadResults() {
      if (!survey) {
        console.log('No survey found, navigating home');
        navigate('/');
        return;
      }

      // In noguard mode, use mock data
      if (noGuard) {
        console.log('[NoGuard Mode] Using mock survey results');
        const mockScores = generateMockScores(survey.id);
        setCategoryScores(mockScores);
        setLoading(false);
        return;
      }

      try {
        console.log('Starting to load results...');
        
        // 1. Get user modules
        console.log('Fetching user modules...');
        const modulesResponse = await apiClient.get('/api/modules/user-modules');
        const modules = modulesResponse.data;
        console.log('Got modules:', modules.length);
        
        // Debug: log all modules to see structure
        console.log('All modules:', modules.map((m: any) => ({
          id: m.id,
          moduleName: m.module?.name,
          surveyId: m.module?.surveyId,
          survey_id: m.module?.survey_id,
          screenType: m.module?.screenType,
          status: m.status
        })));
        
        // 2. Find survey module - either completed or in_progress (just finished)
        const surveyModule = modules.find((m: any) => 
          (m.module.survey_id === survey.id || m.module.surveyId === survey.id) && 
          m.module.screenType === 'survey'
        );

        console.log('Found survey module:', surveyModule);
        console.log('Survey module ID (should be user_module.id):', surveyModule.id);
        console.log('Survey module userId:', surveyModule.userId);
        console.log('Survey module moduleId:', surveyModule.moduleId);

        if (!surveyModule) {
          setError('Survey module not found');
          setLoading(false);
          return;
        }


        // If module is still in_progress, try to complete it
        if (surveyModule.status === 'in_progress') {
          console.log('Module still in progress, attempting to complete it');
          
          try {
            // Check if we have responses first
            const responsesResponse = await apiClient.get(`/api/survey/${survey.id}/responses/${surveyModule.id}`);
            const responses = responsesResponse.data;
            console.log('Responses found:', responses.length);
            
            if (responses && responses.length > 0) {
              // Try to complete the module
              console.log('Attempting to complete module', surveyModule.id);
              await completeUserModule(surveyModule.id);
              console.log('Module completed!');
              
              // Reload to get fresh data
              window.location.reload();
              return;
            } else {
              setError(`No responses found for module ${surveyModule.id}`);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error:', error);
            setError(`Module is in progress but couldn't complete it. Module ID: ${surveyModule.id}`);
            setLoading(false);
            return;
          }
        }

        // 3. Get survey questions
        const questionsResponse = await apiClient.get(`/api/survey/${survey.id}/questions`);
        const questions = questionsResponse.data;

        // 4. Get responses
        const responseUrl = `/api/survey/${survey.id}/responses/${surveyModule.id}`;
        console.log('Fetching responses from:', responseUrl);
        const responsesResponse = await apiClient.get(responseUrl);
        const responses = responsesResponse.data;
        
        console.log('Got responses:', responses.length);

        // 5. Calculate scores by category
        const categoryMap = new Map<number, CategoryScore>();
        
        // Category names
        const categoryNames: Record<number, string> = survey.id === 1 ? {
          1: 'Understanding',
          2: 'Support',
          3: 'Impact',
          4: 'Safety',
          5: 'Readiness',
          6: 'Resources',
          7: 'Outcomes',
          8: 'Alignment',
          9: 'Communication',
          10: 'Adaptation',
          11: 'Implementation',
          12: 'Approach',
          13: 'Guidance',
          14: 'Initiative Understanding',
          15: 'Information Need',
          16: 'Trust'
        } : {
          18: 'Extraversion',
          19: 'Agreeableness',
          20: 'Openness',
          21: 'Conscientiousness',
          22: 'Neuroticism'
        };

        // Process questions and responses
        console.log('Processing questions and responses...');
        console.log('Total questions:', questions.length);
        console.log('Total responses:', responses.length);
        
        questions.forEach((question: any) => {
          const response = responses.find((r: any) => r.questionId === question.id);
          
          if (response) {
            console.log(`Question ${question.id}: response = ${response.response}, categoryId = ${question.categoryId}`);
          }
          
          if (response && response.response > 0) {
            if (!categoryMap.has(question.categoryId)) {
              categoryMap.set(question.categoryId, {
                categoryId: question.categoryId,
                categoryName: categoryNames[question.categoryId] || `Category ${question.categoryId}`,
                score: 0,
                maxScore: 0,
                percentage: 0,
                questionCount: 0
              });
            }
            
            const category = categoryMap.get(question.categoryId)!;
            category.score += response.response;
            category.maxScore += 5; // Assuming 5-point scale
            category.questionCount += 1;
          }
        });
        
        console.log('Categories found:', categoryMap.size);
        console.log('Category map:', Array.from(categoryMap.entries()));

        // Calculate percentages
        const scores = Array.from(categoryMap.values()).map(cat => ({
          ...cat,
          percentage: Math.round((cat.score / cat.maxScore) * 100)
        }));

        setCategoryScores(scores);
        
        // If no scores calculated, show raw data for debugging
        if (scores.length === 0) {
          console.error('No category scores calculated!');
          console.log('Sample questions:', questions.slice(0, 3));
          console.log('Sample responses:', responses.slice(0, 3));
        }
      } catch (err: any) {
        console.error('Error loading results:', err);
        setError('Failed to load results');
      } finally {
        setLoading(false);
      }
    }

    loadResults();
  }, [survey, navigate, noGuard]);

  if (!survey) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Loading results...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle>Error</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600">{error}</p>
            <Button 
              className="mt-4"
              onClick={() => (noGuard ? noGuardNavigate : navigate)('/')}
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const getScoreColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-blue-600';
    if (percentage >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreIcon = (percentage: number) => {
    if (percentage >= 80) return <TrendingUp className="h-5 w-5 text-green-600" />;
    if (percentage >= 40) return <Minus className="h-5 w-5 text-yellow-600" />;
    return <TrendingDown className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{survey.name} Survey Results</h1>
          <p className="text-muted-foreground">{survey.description}</p>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              Survey Complete
            </CardTitle>
            <CardDescription>
              Here's your detailed breakdown by category
            </CardDescription>
          </CardHeader>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          {categoryScores.map((category, index) => (
            <motion.div
              key={category.categoryId}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{category.categoryName}</span>
                    {getScoreIcon(category.percentage)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm text-muted-foreground">
                        Score: {category.score}/{category.maxScore}
                      </span>
                      <span className={`text-sm font-semibold ${getScoreColor(category.percentage)}`}>
                        {category.percentage}%
                      </span>
                    </div>
                    <Progress value={category.percentage} className="h-3" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-8 text-center"
        >
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4">Ready to explore your results?</h3>
              <p className="text-muted-foreground mb-6">
                Your coach is ready to help you understand these insights and develop strategies 
                tailored to your unique profile.
              </p>
              <Button 
                size="lg" 
                onClick={() => (noGuard ? noGuardNavigate : navigate)('/coaching/session/new')}
                className="w-full sm:w-auto"
              >
                Continue to Coaching Session
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
}